import { useDSDFiles } from "@/hooks/use-dsd-files";
import { DsdFile, DsdFileStatus } from "@/types/dsd";
import { ReportPeriod, ReportType } from "@/components/common/report-chip";
import { openUploadDsdModal } from "@/components/modals/upload-dsd";
import {
  VersionUpdateModal,
  type VersionUpdateTarget,
} from "@/components/modals/version-update";
import { useUserNames } from "@/hooks/queries/use-user-names";
import { pushModal } from "@/stores/modal";
import { clearNewReport, useNewReportsStore } from "@/stores/new-reports";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import { getFormattedTimeWithDate, getRelativeTime } from "@/utils/time";
import { getAuthorDisplayName } from "@/utils/user-display-name";
import { ArrowDownWideNarrow } from "lucide-react";
import { useMemo, useState } from "react";
import { useOpenReport } from "@/hooks/use-open-report";
import { groupReports } from "./group-reports";
import { ReportList } from "./report-list";
import { ReportRowMenu } from "./report-row-menu";
import { ReportTypeFilter } from "./report-type-filter";
import { ToolbarButton } from "./toolbar-button";

type SortDir = "desc" | "asc";

const SORT_LABELS: Record<SortDir, string> = {
  desc: "최신순",
  asc: "오래된순",
};

const yearOf = (file: DsdFile): number =>
  file.reportYear ?? new Date(file.createdAt).getFullYear();

const formatDate = (iso: string) =>
  getFormattedTimeWithDate(iso).replace(/\s/g, "").replace(/\.$/, "");

// ============================================
// ReportListContainer
// DsdFile 목록을 버전 그룹으로 묶고, 연도·종류·정렬 상태와 행 액션을 담당.
// 표현은 <ReportList>에 위임한다.
// ============================================

export const ReportListContainer = () => {
  const openReport = useOpenReport();
  const { data: dsdFiles, isLoading } = useDSDFiles({
    status: DsdFileStatus.DSD_ANALYZED,
  });

  const files = useMemo(() => dsdFiles ?? [], [dsdFiles]);

  const creatorEmails = useMemo(() => files.map((f) => f.creator), [files]);
  // isLoading은 enabled로 막 전환된 렌더 틱에 아직 false일 수 있어(effect가 아직 안
  // 돌아서) 그 순간 이메일이 스켈레톤 없이 그려진다. 맵 데이터 유무로 직접 판단한다.
  const { data: userNamesMap } = useUserNames(creatorEmails);
  const isAuthorLoading = creatorEmails.length > 0 && !userNamesMap;

  const fileById = useMemo(() => {
    const map = new Map<string, DsdFile>();
    files.forEach((f) => map.set(f.id, f));
    return map;
  }, [files]);

  // 버전 스택은 필터보다 **먼저** 묶는다. 걸러진 목록으로 묶으면 필터에 걸린 버전에서
  // 체인이 끊겨 같은 보고서가 여러 그룹으로 쪼개지고, 버전마다 reportYear가 다르면
  // 한 스택이 연도 탭을 넘나든다. 묶은 뒤 그룹의 최신 버전 기준으로만 거른다.
  const allGroups = useMemo(
    () =>
      groupReports(files, {
        resolveAuthorName: (email) => getAuthorDisplayName(userNamesMap, email),
        formatDate,
        formatRelativeTime: getRelativeTime,
      }),
    [files, userNamesMap],
  );

  // 방금 업로드된 보고서 — 어느 그룹/연도에 생겼는지 점으로 알려준다.
  const newReportIds = useNewReportsStore((state) => state.ids);
  const newGroupKeys = useMemo(() => {
    const keys = new Set<string>();
    if (newReportIds.size === 0) return keys;
    allGroups.forEach((group) => {
      // 버전 업데이트로 올린 파일은 기존 그룹의 최신 버전이 된다 — 그룹 안 어느
      // 버전이든 새 것이면 그룹에 점을 찍는다.
      const isNew = [group.latest, ...group.olderVersions].some((version) =>
        newReportIds.has(version.id),
      );
      if (isNew) keys.add(group.key);
    });
    return keys;
  }, [allGroups, newReportIds]);

  // 그룹의 연도 = 최신 버전의 연도. 스택 전체가 이 한 값으로 한 탭에 머문다.
  const groupYear = useMemo(() => {
    const byKey = new Map<string, number>();
    allGroups.forEach((group) => {
      const latest = fileById.get(group.latest.id);
      if (latest) byKey.set(group.key, yearOf(latest));
    });
    return byKey;
  }, [allGroups, fileById]);

  const availableYears = useMemo(() => {
    const years = new Set(groupYear.values());
    return Array.from(years).sort((a, b) => b - a);
  }, [groupYear]);

  const newYears = useMemo(() => {
    const years = new Set<number>();
    newGroupKeys.forEach((key) => {
      const year = groupYear.get(key);
      if (year !== undefined) years.add(year);
    });
    return years;
  }, [newGroupKeys, groupYear]);

  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Set<ReportType>>(
    () => new Set(),
  );
  const [selectedPeriods, setSelectedPeriods] = useState<Set<ReportPeriod>>(
    () => new Set(),
  );
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggleInSet =
    <T,>(setter: (fn: (prev: Set<T>) => Set<T>) => void) =>
    (value: T) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
      });
  const toggleType = toggleInSet(setSelectedTypes);
  const togglePeriod = toggleInSet(setSelectedPeriods);

  // 화면에 실제 반영되는 연도(파생 상태).
  // 사용자가 고른 activeYear가 삭제 등으로 더 이상 존재하지 않으면 가장 최근 연도로
  // 자동 fallback한다. 선택 상태를 서버 데이터(availableYears)에서 파생시켜,
  // mutation(삭제/업로드) 후에도 focus가 항상 유효한 연도에 머물게 한다.
  // (useEffect로 activeYear를 되돌리지 않는다 — 파생으로 처리해 동기화 지옥을 피함.)
  const effectiveYear =
    activeYear !== null && availableYears.includes(activeYear)
      ? activeYear
      : (availableYears[0] ?? new Date().getFullYear());

  const groups = useMemo(() => {
    // 필터는 그룹의 최신 버전 기준이다 — 그룹의 뱃지도 최신 버전에서 오므로 화면에
    // 보이는 것과 거르는 기준이 일치한다.
    const visible = allGroups.filter((group) => {
      if (groupYear.get(group.key) !== effectiveYear) return false;
      // 유형/기간 필터 — 선택된 값이 있으면 그 값만 통과 (미선택 = 전체)
      const type = group.financialStatementType;
      const period = group.reportPeriod;
      if (selectedTypes.size > 0 && (!type || !selectedTypes.has(type))) {
        return false;
      }
      if (
        selectedPeriods.size > 0 &&
        (!period || !selectedPeriods.has(period))
      ) {
        return false;
      }
      return true;
    });

    // 그룹 정렬: 최신 버전의 생성(업로드) 시각 기준. 수정 시각으로 정렬하면 태깅만 해도
    // 순서가 흔들리는데, 최근 작업한 보고서는 위쪽 "최근 수정한 DSD 워크북"가 이미 보여준다.
    const sorted = [...visible].sort((a, b) => {
      const aTime = fileById.get(a.latest.id)?.createdAt ?? "";
      const bTime = fileById.get(b.latest.id)?.createdAt ?? "";
      const diff = aTime.localeCompare(bTime);
      return sortDir === "desc" ? -diff : diff;
    });

    return sorted.map((group) => ({
      ...group,
      isNew: newGroupKeys.has(group.key),
    }));
  }, [
    allGroups,
    groupYear,
    effectiveYear,
    selectedTypes,
    selectedPeriods,
    sortDir,
    fileById,
    newGroupKeys,
  ]);

  // 파일 선택은 모달 내부 드롭존이 담당.
  // dim 클릭으로는 닫지 않는다(고른 파일이 날아가는 오조작 방지). 의도가 분명한 ESC 는 허용.
  const openVersionUpdate = (report: VersionUpdateTarget) => {
    pushModal(VersionUpdateModal, {
      report,
      closeOnPointerDownOutside: false,
    });
  };

  const typeControl = (
    <ReportTypeFilter
      selectedTypes={selectedTypes}
      selectedPeriods={selectedPeriods}
      onToggleType={toggleType}
      onTogglePeriod={togglePeriod}
    />
  );

  const sortControl = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ToolbarButton
          className="shadow-sm"
          rightIcon={<ArrowDownWideNarrow className="size-4" />}
        >
          {SORT_LABELS[sortDir]}
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(Object.keys(SORT_LABELS) as SortDir[]).map((value) => (
          <DropdownMenuItem key={value} onClick={() => setSortDir(value)}>
            {SORT_LABELS[value]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <ReportList
      years={availableYears.length > 0 ? availableYears : [effectiveYear]}
      activeYear={effectiveYear}
      onYearChange={setActiveYear}
      newYears={newYears}
      groups={groups}
      isLoading={isLoading}
      onOpenVersion={(version) => {
        // 한 번 열어봤으면 "새로 올라온 것" 표시는 역할을 다했다.
        clearNewReport(version.id);
        openReport(version.id);
      }}
      onVersionUpdate={(group) =>
        openVersionUpdate({
          id: group.latest.id,
          name: group.latest.name,
          type: group.financialStatementType,
          period: group.reportPeriod,
          currentVersion: group.latest.version,
        })
      }
      onUpload={openUploadDsdModal}
      renderMenu={(version) => {
        const file = fileById.get(version.id);
        if (!file) return null;
        return <ReportRowMenu file={file} />;
      }}
      typeControl={typeControl}
      sortControl={sortControl}
      isAuthorLoading={isAuthorLoading}
    />
  );
};
